<template>
  <p
    v-if="!!readonly"
    class="InputText__readonly"
    :data-testid="`${id}-input-text-readonly`"
    :empty="(!modelValue && !$slots.default) || null"
  >
    <slot>
      <span>{{ modelValue ? modelValue : readonlyPlaceholder }}</span>
    </slot>
  </p>

  <span v-else :style="{ width: '100%' }">
    <label v-if="label" :data-testid="`${id}-input-text-label`" :for="`${id}-label`" class="InputText__label">{{
      label
    }}</label>
    <div
      class="InputText"
      :disabled="disabled || null"
      :theme="(error ? 'error' : theme) || null"
      :size="size"
      :data-testid="`${id}-input-text-container`"
    >
      <NovaIcon v-if="leftIcon" name="search" :size="18" />
      <textarea
        :id="id"
        ref="textareaElement"
        type="text"
        class="InputText__input"
        :data-testid="`${id}-input-text`"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :rows="rows || 5"
        @input="handleInput"
      />
      <button
        v-if="modelValue && !disabled"
        class="InputText__clear-btn"
        :data-testid="`${id}-input-text-clear-btn`"
        @click="handleClear"
      >
        <NovaIcon name="close" :size="12" />
      </button>

      <div v-if="error || theme === 'error'" class="InputText__error-icon" data-testid="input-text-error-icon">
        <NovaIcon name="alert" :size="12" />
      </div>

      <div v-else-if="theme === 'success'" class="InputText__success-icon" data-testid="input-text-success-icon">
        <NovaIcon name="check" :size="12" />
      </div>
    </div>
    <div v-if="error" class="flex justify-between items-center mt-1 w-full">
      <span class="InputText__error-msg text-error-110 font-medium text-xs" :data-testid="`${id}-input-text-error`">{{
        error
      }}</span>
    </div>
  </span>
</template>

<script lang="ts" setup>
import { ref, toRef } from "vue";
import NovaIcon from "@/ui-kit/NovaIcon/NovaIcon.vue";
import { useTextareaAutosize } from "@vueuse/core";

export interface Props {
  id: string;
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  leftIcon?: "search";
  theme?: "success" | "error" | "warning" | "default";
  error?: string;
  label?: string;
  size?: "sm" | "lg";
  rows?: number;
  autoresize?: boolean;
  readonly?: boolean;
  readonlyPlaceholder?: string;
}

interface Events {
  (e: "update:modelValue", value: string): void;
  (e: "clear"): void;
}

const props = defineProps<Props>();
const emits = defineEmits<Events>();

const textareaElement = ref<HTMLTextAreaElement>();

// Auto-resize textarea if enabled
if (props.autoresize && !props.readonly) {
  useTextareaAutosize({
    element: textareaElement,
    input: toRef(props, "modelValue"),
  });
}

const handleInput = (event: Event) => {
  if (event.target) {
    const { value } = event.target as HTMLInputElement;
    emits("update:modelValue", value);
  }
};

const handleClear = () => {
  emits("update:modelValue", "");
  emits("clear");
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities";

.InputText {
  --border-color: var(--color-grey-100);

  width: 100%;
  padding: 0 rem(5);
  border: var(--border-default);
  border-color: var(--border-color);
  border-radius: var(--border-radius-default);
  position: relative;
  display: flex;
  align-items: center;
  background-color: white;

  & > svg {
    flex-shrink: 0;
  }

  &__input {
    margin-left: rem(5);
    border: none;
    outline: none;
    width: 100%;
    background-color: transparent;
    color: var(--color-text-100);
    resize: none;
    text-align: left;

    @include font-regular(14);

    &::placeholder {
      font-style: italic;
      color: var(--color-text-70);
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &:hover {
    --border-color: var(--color-text-80);
  }

  &:active {
    --border-color: var(--color-primary-100);
  }

  &:focus-within {
    --border-color: var(--color-primary-100);
  }

  &[disabled] {
    background: #f9f9fb;

    &:hover {
      border-color: var(--color-grey-100);
    }
  }

  &[theme="error"] {
    --border-color: var(--color-error-100);
  }

  &[theme="success"] {
    --border-color: var(--color-success-100);
  }

  &[theme="warning"] {
    --border-color: var(--color-warning-100);
  }

  &__label {
    position: relative;
    bottom: 5px;

    @include font-regular(12);

    color: var(--color-text-90);
  }

  &__clear-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: rem(5) rem(3);
    position: relative;
    margin-right: 5px;

    & > svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &__error-icon {
    color: var(--color-error-100);
    padding: rem(5) rem(3);
  }

  &__success-icon {
    color: var(--color-success-100);
    padding: rem(5) rem(3);
  }

  &[size="lg"] {
    padding: 5px rem(7);
  }

  // Matches the library "Text box" primitive (OFFER-library node 10814:687781) and the RAW
  // edit text box: 8px horizontal + 4px vertical inset, with the neutral idle border. The 8px
  // is the true text inset, so the inner input's default 5px left margin is removed. With
  // autoresize the box grows with content (no max height) while keeping the 4px top/bottom
  // breathing room around the 14/18 text.
  &[size="sm"] {
    --border-color: var(--color-neutral-50);

    padding: rem(4) rem(8);

    .InputText__input {
      margin-left: 0;
    }

    // The size variant sets its own idle border color, so it must not override the feedback
    // themes — keep the error/success/warning border winning even at size="sm".
    &[theme="error"] {
      --border-color: var(--color-error-100);
    }

    &[theme="success"] {
      --border-color: var(--color-success-100);
    }

    &[theme="warning"] {
      --border-color: var(--color-warning-100);
    }
  }

  &__readonly {
    @include font-regular(14);

    &[empty] {
      font-style: italic;
      color: var(--color-text-70);
    }
  }
}
</style>
